import { ConfigurationTrace } from '../components/tracabilite/ConfigurationTrace';
import { ReprisesDepart } from '../components/admin/ReprisesDepart';
import ParametresCnss from '../components/shared/ParametresCnss';
import { useEffect, useState } from 'react';
import { Upload, Save, ShieldAlert, Trash2, Plus } from '../lib/icons';
import { toast } from 'sonner';
import { getBranding, setBranding, BRANDING_DEFAUT } from '../lib/branding';
import { getTarifs, setTarifs, TARIFS_DEFAUT } from '../lib/tarifs';
import { getAutoValidation, setAutoValidation } from '../lib/validation';
import { fetchFormats, createFormat, updateFormat, deleteFormat, derogationsMinimum } from '../lib/dataProvider';
import { getSmsConfig, setSmsConfig } from '../lib/sms';
import { getSouches, setSouches, SOUCHES_DEFAUT, TYPES_DOC, prochainNumero, compterDocuments } from '../lib/souches';
import { compresserImage } from '../lib/image';
import { useAuth } from '../lib/auth';
import { PageTitle } from '../components/shared/badges';
import { reinitialiserDemo, exporterReferentiel, importerReferentiel } from '../lib/store';
import { SauvegardesEtActivite } from '../components/admin/SauvegardesEtActivite';
import { SoldesOuverture } from '../components/admin/SoldesOuverture';
import { ReinitialisationDialog } from '../components/admin/ReinitialisationDialog';

const inputCls =
  'mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

export default function Parametres() {
  const [f, setF] = useState(getBranding());
  const [t, setT] = useState(getTarifs());
  const [formats, setFormats] = useState([]);
  const [nf, setNf] = useState({ nom: '', litres: '', prixEmballage: '', stock: '' });
  const [sms, setSms] = useState(getSmsConfig());
  const [souches, setS] = useState(getSouches());
  const [autoValid, setAutoValid] = useState(getAutoValidation());
  const [reinit, setReinit] = useState(false);
  const [derogations, setDerogations] = useState([]);

  useEffect(() => {
    setDerogations(derogationsMinimum());
  }, []);

  const enregistrerSouches = () => {
    const next = Object.fromEntries(
      TYPES_DOC.map((d) => {
        const s = souches[d.id] ?? SOUCHES_DEFAUT[d.id];
        return [
          d.id,
          {
            prefixe: String(s.prefixe ?? '').trim(),
            depart: Math.max(1, Number(s.depart) || 1),
            longueur: Math.min(8, Math.max(1, Number(s.longueur) || 4)),
          },
        ];
      })
    );
    setSouches(next);
    setS(next);
    toast.success('Souches de numérotation enregistrées');
  };

  const enregistrerSms = () => {
    setSmsConfig({
      actif: Boolean(sms.actif),
      fournisseur: sms.fournisseur.trim() || 'Orange SMS API',
      url: sms.url.trim(),
      token: sms.token.trim(),
      expediteur: sms.expediteur.trim(),
      gabarit: sms.gabarit,
    });
    toast.success(
      sms.actif && sms.url && sms.token
        ? 'Notifications SMS activées'
        : 'Paramètres SMS enregistrés — envoi automatique inactif tant que l’URL et la clé ne sont pas renseignées'
    );
  };

  useEffect(() => {
    fetchFormats().then(setFormats);
  }, []);

  const rechargerFormats = () => fetchFormats().then(setFormats);

  const ajouterFormat = async () => {
    const { error } = await createFormat(nf);
    if (error) return toast.error(error);
    toast.success(`Format « ${nf.nom} » ajouté`);
    setNf({ nom: '', litres: '', prixEmballage: '', stock: '' });
    rechargerFormats();
  };

  const majFormat = async (id, patch) => {
    await updateFormat(id, patch);
    rechargerFormats();
  };

  const supprimerFormat = async (f) => {
    const { error } = await deleteFormat(f.id);
    if (error) return toast.error(error);
    toast.success(`Format « ${f.nom} » supprimé`);
    rechargerFormats();
  };
  const { user } = useAuth();
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setTarif = (k) => (e) => setT((p) => ({ ...p, [k]: e.target.value }));

  const enregistrerTarifs = () => {
    const next = {
      triturationNoble: Number(t.triturationNoble) || 0,
      triturationLampante: Number(t.triturationLampante) || 0,
      retenue: Number(t.retenue),
      calamitesDefaut: t.calamitesDefaut === true,
      prixMarcheHuile: Number(t.prixMarcheHuile) || 0,
      prixMarcheLampante: Number(t.prixMarcheLampante) || 0,
      minimumActif: t.minimumActif === true,
      minimumSeuilKg: Number(t.minimumSeuilKg) || 0,
    };
    if (!next.triturationNoble) return toast.error('Le prix de trituration noble (Extra / Vierge) est obligatoire');
    if (!next.triturationLampante) return toast.error('Le prix de trituration lampante est obligatoire');
    if (!(next.retenue >= 0 && next.retenue <= 100)) return toast.error('Le taux de retenue doit être entre 0 et 100 %');
    if (!(next.minimumSeuilKg > 0 && next.minimumSeuilKg <= 100000))
      return toast.error('Le seuil du minimum de facturation doit être supérieur à 0 kg');
    setT(setTarifs(next));
    toast.success('Grille tarifaire enregistrée');
  };

  if (user?.role !== 'Administrateur') {
    return (
      <div data-testid="parametres-refus" className="flex items-start gap-3 border-2 border-red-600 bg-red-50 px-4 py-4">
        <ShieldAlert size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
        <div>
          <p className="font-heading text-base font-bold text-red-800">Accès réservé à l'administrateur</p>
          <p className="mt-1 text-sm text-red-700">
            Les paramètres d'identité de l'huilerie ne sont modifiables que depuis un compte
            Administrateur.
          </p>
        </div>
      </div>
    );
  }

  const charger = async (file, champ) => {
    if (!file) return;
    try {
      const data = await compresserImage(file, champ === 'logo' || champ === 'loginLogo' ? 420 : 1400, 0.7);
      setF((p) => ({ ...p, [champ]: data }));
      toast.success('Image chargée');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const enregistrer = (e) => {
    e.preventDefault();
    if (!f.nom.trim()) return toast.error('Saisissez le nom de l’entreprise');
    setBranding({ ...f, nom: f.nom.trim() });
    toast.success('Identité visuelle enregistrée');
  };

  return (
    <div data-testid="parametres-page" className="space-y-6">
      <ConfigurationTrace />
      <PageTitle
        emoji="⚙️"
        titre="Paramètres de l'huilerie"
        sous="Grille tarifaire, catalogue des formats, notifications SMS, identité visuelle et mentions légales"
      />

      <ParametresCnss />

      <section data-testid="grille-tarifaire" className="border border-slate-200 border-l-2 border-l-emerald-600 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">
            Grille tarifaire par défaut
          </p>
          <p className="text-xs text-slate-500">
            Prix de trituration différencié par filière, appliqué automatiquement à chaque réception
            client selon la qualité d'huile du lot. Il n'est pas modifiable par le pont bascule. Les
            prix d'achat d'olives (prix de base de l'huile, prix fixe) sont négociés et saisis
            directement sur chaque opération d'achat.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-3">
          <div>
            <label className={label}>Prix trituration NOBLE — Extra / Vierge (TND / kg)</label>
            <input
              data-testid="tarif-trituration-noble"
              type="number"
              step="0.001"
              min="0"
              value={t.triturationNoble}
              onChange={setTarif('triturationNoble')}
              className={inputCls}
            />
            <p className="mt-1.5 font-data text-[11px] text-slate-500">
              Appliqué aux lots Extra-Vierge et Vierge.
            </p>
          </div>
          <div>
            <label className={label}>Prix trituration LAMPANTE (TND / kg)</label>
            <input
              data-testid="tarif-trituration-lampante"
              type="number"
              step="0.001"
              min="0"
              value={t.triturationLampante}
              onChange={setTarif('triturationLampante')}
              className={inputCls}
            />
            <p className="mt-1.5 font-data text-[11px] text-slate-500">
              Appliqué aux lots de la filière lampante.
            </p>
          </div>
          <div>
            <label className={label}>Taux de retenue huilerie (%)</label>
            <input
              data-testid="tarif-retenue"
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={t.retenue}
              onChange={setTarif('retenue')}
              className={inputCls}
            />
            <p className="mt-1.5 font-data text-[11px] text-slate-500">
              Appliqué à la sortie d'huile (achats au rendement, prix fixe, prestations stockées) et
              crédité au compte « Bonus &amp; Garde ».
            </p>
          </div>
          <div>
            <label className={label}>Prix de référence marché — huile noble (TND/kg)</label>
            <input data-testid="tarif-prix-marche" type="number" step="0.001" min="0" value={t.prixMarcheHuile} onChange={setTarif('prixMarcheHuile')} className={inputCls} />
            <p className="mt-1.5 font-data text-[11px] text-slate-500">Base des écarts Bonus/Malus (OF, fiches fournisseurs) et des verdicts du Copilote pour les vierges / extra-vierges.</p>
          </div>
          <div>
            <label className={label}>Prix de référence marché — huile lampante (TND/kg)</label>
            <input data-testid="tarif-prix-marche-lampante" type="number" step="0.001" min="0" value={t.prixMarcheLampante} onChange={setTarif('prixMarcheLampante')} className={inputCls} />
            <p className="mt-1.5 font-data text-[11px] text-slate-500">Utilisé pour les OF et lots de la filière lampante.</p>
          </div>
          <div>
            <label className={label}>Fonds des Calamités (1 %) — achats d’olives</label>
            <label
              data-testid="toggle-calamites-defaut"
              className={`mt-1.5 flex cursor-pointer items-start gap-3 border px-3 py-2.5 transition-colors duration-150 ${
                t.calamitesDefaut ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:bg-slate-50'
              }`}
            >
              <input
                data-testid="input-calamites-defaut"
                type="checkbox"
                checked={t.calamitesDefaut === true}
                onChange={(e) => setT((p) => ({ ...p, calamitesDefaut: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-slate-900"
              />
              <span data-testid="etat-calamites-defaut" className="text-sm font-semibold text-slate-900">
                {t.calamitesDefaut ? 'Activée par défaut' : 'Désactivée par défaut'}
              </span>
            </label>
            <p className="mt-1.5 font-data text-[11px] text-slate-500">
              Précoche (ou non) la retenue de 1 % sur tout nouvel achat d’olives. Reste modifiable au
              cas par cas dans la réception et le décompte. Sans retenue, le fonds reste à charge de l’huilerie.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 border-t border-slate-200 px-4 py-4">
          <button
            type="button"
            data-testid="tarifs-enregistrer"
            onClick={enregistrerTarifs}
            className="flex h-11 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Save size={16} /> Enregistrer la grille
          </button>
          <button
            type="button"
            data-testid="tarifs-reinitialiser"
            onClick={() => {
              setT(TARIFS_DEFAUT);
              setTarifs(TARIFS_DEFAUT);
              toast.success('Grille tarifaire réinitialisée');
            }}
            className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Réinitialiser
          </button>
        </div>
      </section>

      <section data-testid="parametres-minimum-trituration" className="border border-slate-200 border-l-2 border-l-sky-700 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">
            Minimum de facturation de la trituration
          </p>
          <p className="mt-1 text-xs text-slate-500">
            La prestation de trituration est facturée au minimum pour le seuil ci-dessous, au tarif de
            la filière du lot. Le complément est <strong>uniquement financier</strong> : le poids pesé,
            les rendements, l’huile attribuée au client et les stocks restent sur le poids réellement
            reçu. Le minimum s’applique <strong>une seule fois par passage</strong> réellement trituré
            ensemble, et un bon déjà validé n’est jamais réécrit par un changement de ce réglage.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2">
          <div>
            <label className={label}>Application du minimum</label>
            <label
              data-testid="toggle-minimum-trituration"
              className={`mt-1.5 flex cursor-pointer items-start gap-3 border px-3 py-2.5 transition-colors duration-150 ${
                t.minimumActif ? 'border-sky-700 bg-sky-50' : 'border-slate-300 bg-white hover:bg-slate-50'
              }`}
            >
              <input
                data-testid="input-minimum-trituration"
                type="checkbox"
                checked={t.minimumActif === true}
                onChange={(e) => setT((p) => ({ ...p, minimumActif: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-sky-700"
              />
              <span>
                <span data-testid="etat-minimum-trituration" className="block text-sm font-semibold text-slate-900">
                  {t.minimumActif ? 'Minimum appliqué par défaut' : 'Minimum désactivé'}
                </span>
                <span className="mt-0.5 block font-data text-[11px] text-slate-500">
                  Réglage global, réservé à l’Administrateur et contrôlé par le serveur. Les clients
                  exemptés sur leur fiche contact ne sont jamais concernés.
                </span>
              </span>
            </label>
          </div>
          <div>
            <label className={label}>Seuil facturé au minimum (kg d’olives)</label>
            <input
              data-testid="minimum-seuil-kg"
              type="number"
              step="1"
              min="1"
              value={t.minimumSeuilKg}
              onChange={setTarif('minimumSeuilKg')}
              className={inputCls}
            />
            <p data-testid="minimum-apercu" className="mt-1.5 font-data text-[11px] text-slate-500">
              Exemple filière noble : un passage de 300 kg à {(Number(t.triturationNoble) || 0).toFixed(3)} TND/kg
              est facturé {(300 * (Number(t.triturationNoble) || 0)).toFixed(3)} TND +{' '}
              {Math.max(0, (Number(t.minimumSeuilKg) || 0) * (Number(t.triturationNoble) || 0) - 300 * (Number(t.triturationNoble) || 0)).toFixed(3)}{' '}
              TND de complément ={' '}
              {Math.max(
                300 * (Number(t.triturationNoble) || 0),
                (Number(t.minimumSeuilKg) || 0) * (Number(t.triturationNoble) || 0)
              ).toFixed(3)}{' '}
              TND.
            </p>
          </div>
        </div>
        <div className="border-t border-slate-200 px-4 py-4">
          <button
            type="button"
            data-testid="minimum-enregistrer"
            onClick={enregistrerTarifs}
            className="flex h-11 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Save size={16} /> Enregistrer le minimum
          </button>
        </div>
      </section>

      <section data-testid="parametres-derogations-minimum" className="border border-slate-200 border-l-2 border-l-amber-700 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <p className="font-heading text-base font-bold tracking-tight text-slate-900">
              Relevé des dérogations au minimum de trituration
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Suivi interne des compléments abandonnés par un utilisateur autorisé. Jamais imprimé sur
              les documents clients. Le droit « Peut déroger au minimum de trituration » se délègue
              dans Administration › Utilisateurs.
            </p>
          </div>
          <button
            type="button"
            data-testid="derogations-actualiser"
            onClick={() => setDerogations(derogationsMinimum())}
            className="h-10 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Actualiser
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-derogations-minimum">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Bon', 'Date', 'Client', 'Poids net', 'Seuil', 'Tarif', 'Complément abandonné', 'Autorisé par', 'Le', 'Motif'].map((c) => (
                  <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {derogations.length === 0 ? (
                <tr data-testid="derogations-vide">
                  <td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-500">
                    Aucune dérogation enregistrée.
                  </td>
                </tr>
              ) : (
                derogations.map((d) => (
                  <tr key={d.id} data-testid={`derogation-row-${d.id}`} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-data text-xs font-bold text-slate-900">{d.id}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-600">{d.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-900">{d.tiers}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-700">{Number(d.poidsNet || 0).toFixed(0)} kg</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-700">{Number(d.seuil || 0).toFixed(0)} kg</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-700">{Number(d.tarif || 0).toFixed(3)}</td>
                    <td data-testid={`derogation-montant-${d.id}`} className="px-3 py-2 font-data text-sm font-semibold text-amber-800">
                      {Number(d.montantAbandonne || 0).toFixed(3)} TND
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">{d.par || '—'}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-600">{d.le ? String(d.le).slice(0, 16).replace('T', ' ') : '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{d.note || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section data-testid="parametres-validation" className="border border-slate-200 border-l-2 border-l-amber-600 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">Circuit de validation</p>
          <p className="mt-1 text-xs text-slate-500">
            Par défaut, les opérations soumises à l’accord du gérant restent « En attente » jusqu’à
            leur validation dans l’ERP.
          </p>
        </div>
        <div className="px-4 py-4">
          <label
            data-testid="toggle-auto-validation"
            className={`flex cursor-pointer items-start gap-3 border px-3 py-3 transition-colors duration-150 ${
              autoValid ? 'border-amber-600 bg-amber-50' : 'border-slate-300 bg-white hover:bg-slate-50'
            }`}
          >
            <input
              data-testid="input-auto-validation"
              type="checkbox"
              checked={autoValid === true}
              onChange={(e) => {
                setAutoValid(e.target.checked);
                setAutoValidation(e.target.checked);
                toast.success(
                  e.target.checked
                    ? 'Validation automatique activée — toute nouvelle opération part directement validée'
                    : 'Validation automatique désactivée — l’accord du gérant redevient obligatoire'
                );
              }}
              className="mt-0.5 h-4 w-4 accent-amber-600"
            />
            <span>
              <span data-testid="etat-auto-validation" className="block text-sm font-bold text-slate-900">
                Validation automatique (bypass gérant) — {autoValid ? 'ACTIVÉE' : 'désactivée'}
              </span>
              <span className="mt-0.5 block font-data text-xs text-slate-500">
                Réglage global partagé par tous les postes. Une fois actif, chaque nouvelle opération
                coursier (frais, tiers, personnel) et chaque ordre de transfert de cuve passent
                directement à l’état validé, avec leur imputation habituelle et une trace
                « Auto-validation » dans le journal d’audit.
              </span>
            </span>
          </label>
        </div>
      </section>

      <section data-testid="parametres-facturation" className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">Paramètres de facturation</p>
          <p className="text-xs text-slate-500">
            Souches de numérotation : définissez le préfixe et le numéro de départ pour enchaîner avec
            votre ancienne comptabilité. Chaque document validé reçoit un numéro unique et incrémental.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-souches">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Type de document', 'Préfixe', 'Numéro de départ', 'Longueur', 'Émis', 'Prochain numéro'].map((c) => (
                  <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TYPES_DOC.map((d) => {
                const s = souches[d.id] ?? SOUCHES_DEFAUT[d.id];
                const maj = (patch) => setS((p) => ({ ...p, [d.id]: { ...s, ...patch } }));
                return (
                  <tr key={d.id} data-testid={`souche-row-${d.id}`} className="border-b border-slate-100">
                    <td className="px-3 py-2">
                      <p className="text-sm font-semibold text-slate-900">{d.label}</p>
                      <p className="text-xs text-slate-500">{d.hint}</p>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        data-testid={`souche-prefixe-${d.id}`}
                        value={s.prefixe}
                        onChange={(e) => maj({ prefixe: e.target.value })}
                        className="h-10 w-36 border border-slate-200 bg-white px-2 font-data text-sm outline-none focus:border-slate-900"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        data-testid={`souche-depart-${d.id}`}
                        type="number"
                        min="1"
                        value={s.depart}
                        onChange={(e) => maj({ depart: e.target.value })}
                        className="h-10 w-28 border border-slate-200 bg-white px-2 font-data text-sm outline-none focus:border-slate-900"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        data-testid={`souche-longueur-${d.id}`}
                        type="number"
                        min="1"
                        max="8"
                        value={s.longueur}
                        onChange={(e) => maj({ longueur: e.target.value })}
                        className="h-10 w-20 border border-slate-200 bg-white px-2 font-data text-sm outline-none focus:border-slate-900"
                      />
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{compterDocuments(d.id)}</td>
                    <td data-testid={`souche-apercu-${d.id}`} className="px-3 py-2 font-data text-sm font-semibold text-slate-900">
                      {prochainNumero(d.id).numero}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3 border-t border-slate-200 px-4 py-4">
          <button
            type="button"
            data-testid="souches-enregistrer"
            onClick={enregistrerSouches}
            className="flex h-11 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Save size={16} /> Enregistrer les souches
          </button>
          <p className="self-center font-data text-[11px] text-slate-500">
            Les numéros déjà attribués ne changent pas : la modification s'applique aux prochains documents.
          </p>
        </div>
      </section>

      <section data-testid="catalogue-formats" className="border border-slate-200 border-l-2 border-l-sky-600 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">Catalogue des formats</p>
          <p className="text-xs text-slate-500">
            Contenants de conditionnement : ajout, modification et suppression — utilisés par la mise en
            bouteille et la vente au comptoir
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-formats">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Désignation', 'Contenance (L)', 'Prix emballage (TND)', 'Stock emballages vides', ''].map((c) => (
                  <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formats.map((f) => (
                <tr key={f.id} data-testid={`format-row-${f.id}`} className="border-b border-slate-100">
                  <td className="px-3 py-2">
                    <input
                      data-testid={`format-nom-${f.id}`}
                      defaultValue={f.nom}
                      onBlur={(e) => majFormat(f.id, { nom: e.target.value })}
                      className="h-10 w-full border border-slate-200 bg-white px-2 text-sm outline-none focus:border-slate-900"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`format-litres-${f.id}`}
                      type="number"
                      step="0.1"
                      defaultValue={f.litres}
                      onBlur={(e) => majFormat(f.id, { litres: e.target.value })}
                      className="h-10 w-24 border border-slate-200 bg-white px-2 font-data text-sm outline-none focus:border-slate-900"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`format-prix-${f.id}`}
                      type="number"
                      step="0.001"
                      defaultValue={f.prixEmballage}
                      onBlur={(e) => majFormat(f.id, { prixEmballage: e.target.value })}
                      className="h-10 w-28 border border-slate-200 bg-white px-2 font-data text-sm outline-none focus:border-slate-900"
                    />
                  </td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{f.stock}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`format-supprimer-${f.id}`}
                      onClick={() => supprimerFormat(f)}
                      className="border border-slate-300 p-2 text-slate-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4 md:grid-cols-5">
          <input data-testid="nouveau-format-nom" placeholder="Désignation" value={nf.nom} onChange={(e) => setNf({ ...nf, nom: e.target.value })} className={inputCls} />
          <input data-testid="nouveau-format-litres" type="number" step="0.1" placeholder="Litres" value={nf.litres} onChange={(e) => setNf({ ...nf, litres: e.target.value })} className={inputCls} />
          <input data-testid="nouveau-format-prix" type="number" step="0.001" placeholder="Prix emballage" value={nf.prixEmballage} onChange={(e) => setNf({ ...nf, prixEmballage: e.target.value })} className={inputCls} />
          <input data-testid="nouveau-format-stock" type="number" step="1" placeholder="Stock emballages vides" value={nf.stock} onChange={(e) => setNf({ ...nf, stock: e.target.value })} className={inputCls} />
          <button
            type="button"
            data-testid="nouveau-format-ajouter"
            onClick={ajouterFormat}
            className="flex h-11 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Plus size={15} /> Ajouter le format
          </button>
        </div>
      </section>

      <section data-testid="parametres-sms" className="border border-slate-200 border-l-2 border-l-amber-500 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">Notifications SMS</p>
          <p className="text-xs text-slate-500">
            Envoi automatique du lien de suivi au client dès l'enregistrement de sa réception. En attente
            des identifiants du fournisseur (Orange SMS API ou équivalent) — le partage manuel
            SMS / WhatsApp reste disponible entre-temps.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2">
          <div>
            <label className={label}>Fournisseur</label>
            <input data-testid="sms-fournisseur" value={sms.fournisseur} onChange={(e) => setSms({ ...sms, fournisseur: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={label}>URL de l'API</label>
            <input data-testid="sms-url" value={sms.url} onChange={(e) => setSms({ ...sms, url: e.target.value })} placeholder="https://api.orange.com/smsmessaging/v1/..." className={inputCls} />
          </div>
          <div>
            <label className={label}>Clé / Token</label>
            <input data-testid="sms-token" type="password" value={sms.token} onChange={(e) => setSms({ ...sms, token: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={label}>Numéro expéditeur</label>
            <input data-testid="sms-expediteur" value={sms.expediteur} onChange={(e) => setSms({ ...sms, expediteur: e.target.value })} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Gabarit du message</label>
            <input data-testid="sms-gabarit" value={sms.gabarit} onChange={(e) => setSms({ ...sms, gabarit: e.target.value })} className={inputCls} />
            <p className="mt-1.5 font-data text-[11px] text-slate-500">
              Variables disponibles : {'{lot}'} · {'{poids}'} · {'{lien}'} · {'{nom}'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-4 py-4">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <input data-testid="sms-actif" type="checkbox" checked={Boolean(sms.actif)} onChange={(e) => setSms({ ...sms, actif: e.target.checked })} className="h-4 w-4 accent-slate-900" />
            Activer l'envoi automatique
          </label>
          <button
            type="button"
            data-testid="sms-enregistrer"
            onClick={enregistrerSms}
            className="flex h-11 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Save size={16} /> Enregistrer les notifications
          </button>
        </div>
      </section>

      {user?.role === 'Administrateur' && (
        <section className="border border-slate-200 bg-white px-4 py-4" data-testid="lien-demarrage-zero">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">
            Démarrage zéro — remise à zéro contrôlée
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Gel des écritures, sauvegarde vérifiée, nettoyage de la base d'exploitation et retour arrière.
            Réservé à l'administrateur, avec confirmation écrite à chaque étape.
          </p>
          <a
            href="/admin/demarrage-zero"
            data-testid="ouvrir-demarrage-zero"
            className="mt-3 inline-flex h-11 items-center bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            Ouvrir la console de démarrage
          </a>
        </section>
      )}

      {user?.role === 'Administrateur' && <><ReprisesDepart /><SoldesOuverture /></>}

      <form onSubmit={enregistrer} className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <section className="border border-slate-200 bg-white">
          <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
            Informations de l'entreprise
          </p>
          <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2">
            <div>
              <label className={label}>Nom de l'entreprise</label>
              <input data-testid="param-nom" value={f.nom} onChange={set('nom')} className={inputCls} />
            </div>
            <div>
              <label className={label}>Slogan / activité</label>
              <input data-testid="param-slogan" value={f.slogan} onChange={set('slogan')} className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className={label}>Adresse physique</label>
              <input data-testid="param-adresse" value={f.adresse} onChange={set('adresse')} className={inputCls} />
            </div>
            <div>
              <label className={label}>Téléphone principal</label>
              <input data-testid="param-tel" value={f.tel} onChange={set('tel')} className={inputCls} />
            </div>
            <div>
              <label className={label}>Téléphone secondaire</label>
              <input data-testid="param-tel2" value={f.tel2 ?? ''} onChange={set('tel2')} className={inputCls} />
            </div>
            <div>
              <label className={label}>Email</label>
              <input data-testid="param-email" value={f.email} onChange={set('email')} className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className={label}>Matricule fiscal (MF)</label>
              <input data-testid="param-mf" value={f.mf} onChange={set('mf')} className={inputCls} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 border-t border-slate-200 px-4 py-4">
            <button
              type="submit"
              data-testid="param-enregistrer"
              className="flex h-11 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
            >
              <Save size={16} /> Enregistrer
            </button>
            <button
              type="button"
              data-testid="param-reinitialiser"
              onClick={() => {
                setF(BRANDING_DEFAUT);
                setBranding(BRANDING_DEFAUT);
                toast.success('Identité réinitialisée');
              }}
              className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
            >
              Réinitialiser
            </button>
          </div>
        </section>

        <section className="space-y-4">
          {[
            { champ: 'logo', titre: 'Logo officiel', hint: 'Affiché dans le menu et en en-tête de tous les documents' },
            { champ: 'banniere', titre: 'Bannière d’accueil', hint: 'Visuel oléicole affiché sur le tableau de bord' },
            { champ: 'loginLogo', titre: 'Logo page de connexion', hint: 'Si vide, le logo officiel est utilisé' },
            { champ: 'loginFond', titre: 'Image de fond du login', hint: 'Visuel affiché sur la page publique de connexion' },
          ].map((x) => (
            <div key={x.champ} className="border border-slate-200 bg-white">
              <p className="border-b border-slate-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                {x.titre}
              </p>
              <div className="space-y-3 px-4 py-4">
                {f[x.champ] ? (
                  <img
                    data-testid={`param-apercu-${x.champ}`}
                    src={f[x.champ]}
                    alt={x.titre}
                    className={`w-full border border-slate-200 ${x.champ === 'logo' || x.champ === 'loginLogo' ? 'h-28 object-contain p-2' : 'h-32 object-cover'}`}
                  />
                ) : (
                  <p className="border border-dashed border-slate-300 px-3 py-8 text-center text-xs text-slate-500">
                    Aucune image
                  </p>
                )}
                <label className="flex h-10 cursor-pointer items-center justify-center gap-2 border border-slate-300 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
                  <Upload size={14} /> Importer une image
                  <input
                    data-testid={`param-fichier-${x.champ}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => charger(e.target.files?.[0], x.champ)}
                  />
                </label>
                <p className="font-data text-[11px] text-slate-500">{x.hint}</p>
              </div>
            </div>
          ))}
        </section>

        <section data-testid="section-referentiel" className="border border-slate-200 border-l-2 border-l-emerald-600 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">
              Référentiel — export & import entre environnements
            </h2>
            <p className="text-xs text-slate-500">
              Contacts & fournisseurs, catalogue produits, tarifs, souches, utilisateurs et paramètres :
              données persistantes, jamais effacées par une réinitialisation. Transférez-les d’un
              environnement à l’autre (aperçu ↔ production) sans toucher aux données de campagne.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-4">
            <button
              type="button"
              data-testid="exporter-referentiel"
              onClick={async () => {
                try {
                  const doc = await exporterReferentiel();
                  toast.success(`Référentiel exporté — ${doc.cles} collection(s), ${doc.lignes} fiche(s) : ${doc.nom}`);
                } catch (e) {
                  toast.error(e.message);
                }
              }}
              className="h-11 bg-slate-900 px-4 font-heading text-sm font-bold text-white hover:bg-slate-700"
            >
              Exporter le référentiel (JSON)
            </button>
            <label
              data-testid="importer-referentiel"
              className="flex h-11 cursor-pointer items-center border-2 border-emerald-700 px-4 font-heading text-sm font-bold text-emerald-800 hover:bg-emerald-700 hover:text-white"
            >
              Importer un référentiel (fusion sans perte)
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  try {
                    const { rapport } = await importerReferentiel(f, 'fusion');
                    const detail = Object.entries(rapport)
                      .map(([cle, r]) => `${cle.replace('oliva.', '')} : ${r.ajoutes} ajout(s), ${r.enrichis} complété(s) → ${r.total}`)
                      .join(' · ');
                    toast.success(`Référentiel importé — ${detail}. Rechargement…`, { duration: 9000 });
                    setTimeout(() => window.location.reload(), 1800);
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
              />
            </label>
            <p className="font-data text-[11px] text-slate-500">
              L’import ne supprime AUCUNE fiche existante : il ajoute les manquantes et complète les
              champs vides. Une sauvegarde de sécurité est créée automatiquement avant l’opération.
            </p>
          </div>
        </section>

        <section data-testid="section-demo" className="border border-slate-200 border-l-2 border-l-red-600 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">
              Base partagée & jeu de démonstration
            </h2>
            <p className="text-xs text-slate-500">
              Les données de l'ERP sont stockées sur le serveur (MongoDB) et partagées entre tous les
              postes. Les remises à zéro partielles sont désactivées ; la bascule complète est réalisée hors ligne avec sauvegarde.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-4">
            <button
              type="button"
              data-testid="reinitialiser-propre"
              disabled title="Utiliser le paquet de démarrage hors ligne"
              className="h-11 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
            >
              Remise à zéro : procédure hors ligne
            </button>
            <p className="font-data text-[11px] text-slate-500">
              Vide les données transactionnelles de la saison. Le référentiel reste intact : contacts &
              fournisseurs, catalogue produits, tarifs, souches, utilisateurs et paramètres.
              Confirmation par mot de passe administrateur, sauvegarde de sécurité automatique.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-4">
            <button
              type="button"
              disabled data-testid="reinitialiser-demo"
              onClick={async () => {
                if (!window.confirm('Effacer les données transactionnelles du serveur et régénérer le jeu de démonstration ?\n\nLe référentiel (contacts & fournisseurs, catalogue, tarifs, paramètres) est conservé.')) return;
                try {
                  await reinitialiserDemo();
                  toast.success('Base réinitialisée — rechargement…');
                  setTimeout(() => window.location.reload(), 900);
                } catch (e) {
                  toast.error(`Réinitialisation impossible : ${e.message}`);
                }
              }}
              className="h-11 border-2 border-red-600 px-4 font-heading text-sm font-bold text-red-700 transition-colors duration-150 hover:bg-red-600 hover:text-white"
            >
              Réinitialiser le jeu de démonstration
            </button>
            <p className="font-data text-[11px] text-slate-500">
              Réceptions, cuves, caisses, ventes et pointages sont remis à l'état d'exemple sur tous les
              postes. Le référentiel est INTOUCHABLE : contacts & fournisseurs, catalogue produits, tarifs,
              souches, utilisateurs et paramètres de l'huilerie sont conservés. Sauvegarde automatique avant l'opération.
            </p>
          </div>
        </section>

      </form>

      <div className="space-y-6">
        <SauvegardesEtActivite />
      </div>
      {reinit && <ReinitialisationDialog onClose={() => setReinit(false)} />}
    </div>
  );
}
